import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanySelector } from "@/hooks/useCompanySelector";
import { toast } from "sonner";
import type { TakeoffOwnerType } from "./useDocumentAnnotations";

export interface TakeoffDocument {
  id: string;
  file_name: string;
  file_path: string;
  mime_type: string | null;
  file_size: number;
  created_at: string;
}

interface UseTakeoffDocumentsOpts {
  projectId?: string;
  estimateId?: string;
}

export function useTakeoffDocuments({ projectId, estimateId }: UseTakeoffDocumentsOpts) {
  const ownerType: TakeoffOwnerType = estimateId ? "estimate" : "project";
  const ownerId = estimateId || projectId;
  const table = ownerType === "estimate" ? "estimate_documents" : "project_documents";
  const fkColumn = ownerType === "estimate" ? "estimate_id" : "project_id";

  const query = useQuery({
    queryKey: ["takeoff_documents", ownerType, ownerId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from(table)
        .select("id, file_name, file_path, mime_type, file_size, created_at")
        .eq(fkColumn, ownerId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return ((data ?? []) as any[]).filter((d: any) => {
        const mt = d.mime_type;
        return mt && (mt.includes("pdf") || mt.startsWith("image"));
      }) as TakeoffDocument[];
    },
    enabled: !!ownerId,
  });

  return { ...query, ownerType };
}

export function useUploadTakeoffDocument() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { activeCompanyId } = useCompanySelector();

  return useMutation({
    mutationFn: async (params: {
      file: File;
      ownerType: TakeoffOwnerType;
      ownerId: string;
      notes?: string;
    }) => {
      if (!activeCompanyId || !user) throw new Error("Not authenticated");

      const filePath = `${activeCompanyId}/${params.ownerId}/${Date.now()}_${params.file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("project-documents")
        .upload(filePath, params.file);
      if (uploadError) throw uploadError;

      const table = params.ownerType === "estimate" ? "estimate_documents" : "project_documents";
      const fkColumn = params.ownerType === "estimate" ? "estimate_id" : "project_id";

      const insertObj: any = {
        [fkColumn]: params.ownerId,
        company_id: activeCompanyId,
        uploaded_by: user.id,
        file_name: params.file.name,
        file_path: filePath,
        file_size: params.file.size,
        mime_type: params.file.type || null,
        notes: params.notes?.trim() || null,
        source: "upload",
      };

      const { error: insertError } = await supabase.from(table as any).insert(insertObj);
      if (insertError) throw insertError;
    },
    onSuccess: (_, vars) => {
      toast.success(`Uploaded "${vars.file.name}"`);
      qc.invalidateQueries({ queryKey: ["takeoff_documents", vars.ownerType, vars.ownerId] });
    },
    onError: (err: any) => toast.error(err.message || "Upload failed"),
  });
}
